import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useMemo} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {Caption, HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import type {AppFeatures} from "@repo/analytics-growthbook/app-features";
import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";
import {getPass} from "@repo/data-cookies";
import {useGql} from "@repo/data-gql";
import {reload} from "@repo/urls";

import TeacherTOSAffirmation from "#class-creator-package/components/teacher-tos-affirmation.tsx";

import type {optInCoach_clearUserOptOutOfABTesting as SetTeacherOptInToResearchData} from "./__graphql-types__/optInCoach.ts";
import type {optOutCoach_userOptOutOfABTesting as SetTeacherOptOutOfResearchData} from "./__graphql-types__/optOutCoach.ts";
import type {setHasFormalTeacherStatus_setSettings as SetTeacherTOSData} from "./__graphql-types__/setHasFormalTeacherStatus.ts";
import {canSaveTOSAcceptance} from "./teacher-affiliation-helpers.ts";
import {
    TEACHER_OPT_IN_TO_RESEARCH_MUTATION,
    TEACHER_OPT_OUT_OF_RESEARCH_MUTATION,
    TEACHER_TOS_MUTATION,
} from "./teacher-affiliation-mutations.ts";

type Props = {
    kaid: string;
    hasFormalTeacherStatus?: boolean | null;
    includesDistrictOwnedData: boolean;
    previouslyOptedOutOfReasearch?: boolean | null;
};

// TODO(bcdirito, CLASS-10198)
// Write spec tests covering behavior
// Must complete before deployment
export default function TeacherAffiliationSection({
    kaid,
    hasFormalTeacherStatus,
    includesDistrictOwnedData,
    previouslyOptedOutOfReasearch,
}: Props): React.ReactElement {
    const gqlFetch = useGql();
    // The isTOSAccepted variable tracks the current state (in the UI).
    // It is initialized from the hasFormalTeacherStatus prop. It controls what
    // is displayed in the UI.
    const [isTOSAccepted, setTOSAccepted] = React.useState(
        hasFormalTeacherStatus ?? false,
    );
    // lastSavedTOSAcceptance tracks the state that is saved on the server.
    // It is also initialized by the hasFormalTeacherStatus prop.
    // It is set only on a successful mutation, and compared against
    // isTOSAccepted to determine whether to enable the save button.
    const [lastSavedTOSAcceptance, setLastSavedTOSAcceptance] = React.useState(
        hasFormalTeacherStatus,
    );

    const [optOutOfResearch, setOptOutOfResearch] = React.useState(
        !!(previouslyOptedOutOfReasearch || false),
    );

    const [lastSavedOptOutOfResearch, setLastSavedOptOutOfResearch] =
        React.useState(previouslyOptedOutOfReasearch);

    // Hides the error message shown after attempting a save. We want
    // to hide the message when the user interacts with the checkbox, but we
    // may still have an error from the parent.
    const [hideSaveErrorMessage, setHideSaveErrorMessage] =
        React.useState(false);

    // Tracks whether the mutation triggered by clicking the save button is
    // loading.
    const [isSaveLoading, setSaveLoading] = React.useState(false);

    // Tracks whether the mutation triggered by clicking the save button
    // encountered an error state.
    const [isSaveError, setSaveError] = React.useState(false);

    const checkboxIsEnabled = useMemo(() => {
        return !!isTOSAccepted;
    }, [isTOSAccepted]);

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const optOutOfResearchFeatureIsOn = useFeatureIsOn<AppFeatures>(
        "tx-settings-opt-out-of-research",
    );
    const optOutOfResearchFlag = getPass("tx-settings-opt-out-of-research");
    const optOutOfResearchEnabled =
        (optOutOfResearchFeatureIsOn && !includesDistrictOwnedData) ||
        optOutOfResearchFlag;

    return (
        <View role="region" aria-label={t`Teacher confirmation settings`}>
            <HeadingSmall style={styles.header} tag="h2">
                {t`Teacher confirmation`}
            </HeadingSmall>
            <Strut size={spacing.medium_16} />
            <TeacherTOSAffirmation
                isLoading={false}
                isError={false}
                hasFormalTeacherStatus={isTOSAccepted}
                isDisabled={isSaveLoading}
                onChangeHasFormalTeacherStatus={(checked: boolean) => {
                    setTOSAccepted(checked);
                    setHideSaveErrorMessage(true);
                    if (!checked) {
                        setOptOutOfResearch(!!lastSavedOptOutOfResearch);
                    } else {
                        setOptOutOfResearch(!!optOutOfResearch);
                    }
                }}
            />
            <Strut size={spacing.large_24} />
            {optOutOfResearchEnabled && (
                <View testId="opt-out-of-research-section">
                    <Tooltip
                        content={t`Research helps improve the Khan Academy experience. Your students may encounter new features or experiences before they are available to all users.`}
                    >
                        <Checkbox
                            testId="opt-out-of-research-checkbox"
                            checked={!optOutOfResearch}
                            id="opt-out-of-research"
                            onChange={(checked: boolean) => {
                                setOptOutOfResearch(!checked);
                            }}
                            description={t`Uncheck to exclude research such as feature testing and efficacy research.`}
                            label={t`My classes may participate in research for product improvement.`}
                            disabled={!checkboxIsEnabled} // disabled if TOS is not accepted
                        />
                    </Tooltip>
                </View>
            )}
            {optOutOfResearchEnabled && <Strut size={spacing.large_24} />}
            <Button
                style={styles.button}
                disabled={
                    !canSaveTOSAcceptance(
                        isTOSAccepted,
                        lastSavedTOSAcceptance,
                    ) &&
                    optOutOfResearch === lastSavedOptOutOfResearch &&
                    !isSaveLoading
                }
                testId="teacher-affiliation-save-button"
                onClick={async () => {
                    setSaveLoading(true);
                    setHideSaveErrorMessage(false);

                    const isUpdatingTOSAcceptanceValue = canSaveTOSAcceptance(
                        isTOSAccepted,
                        lastSavedTOSAcceptance,
                    );

                    const promises: Array<Promise<any>> = [];
                    let updateTOSPromise: Promise<SetTeacherTOSData> | null =
                        null;

                    if (isUpdatingTOSAcceptanceValue) {
                        updateTOSPromise = gqlFetch(TEACHER_TOS_MUTATION, {
                            variables: {
                                tosForFormalTeacherStatus: isTOSAccepted,
                            },
                        });
                        promises.push(updateTOSPromise);
                    }

                    const isUpdatingOptOutValue =
                        checkboxIsEnabled &&
                        optOutOfResearch !== lastSavedOptOutOfResearch;
                    let updateOptOutPromise:
                        | Promise<SetTeacherOptOutOfResearchData>
                        | Promise<SetTeacherOptInToResearchData>
                        | null = null;
                    if (isUpdatingOptOutValue) {
                        if (optOutOfResearch) {
                            updateOptOutPromise = gqlFetch(
                                TEACHER_OPT_OUT_OF_RESEARCH_MUTATION,
                                {
                                    variables: {
                                        kaid: kaid,
                                    },
                                },
                            );
                        } else {
                            updateOptOutPromise = gqlFetch(
                                TEACHER_OPT_IN_TO_RESEARCH_MUTATION,
                                {
                                    variables: {
                                        kaid: kaid,
                                    },
                                },
                            );
                        }
                        promises.push(updateOptOutPromise);
                    }

                    // Set loading state
                    setSaveLoading(true);
                    setSaveError(false);
                    let success = true;
                    // allSettled tells when all of the GraphQL requests
                    // have completed
                    const results = await Promise.allSettled(promises);
                    // Check the results of each promise
                    results.forEach((result, index) => {
                        if (result.status === "fulfilled") {
                            if (promises[index] === updateTOSPromise) {
                                setLastSavedTOSAcceptance(isTOSAccepted);
                                // We only want to publish a CEDAR event if the user is updating
                                // the TOS acceptance status.
                                publishAnalyticsEvent({
                                    eventType:
                                        "SchoolPickerFormalTeacherSelected",
                                    eventSchemaVersion: 2,
                                    isFormalTeacher: isTOSAccepted,
                                });
                            } else if (
                                promises[index] === updateOptOutPromise
                            ) {
                                setLastSavedOptOutOfResearch(optOutOfResearch);
                            }
                        } else if (result.status === "rejected") {
                            success = false;
                            setSaveLoading(false);
                            setSaveError(true);
                            // Break out of the loop
                            // if one of the promises fails
                            return;
                        }
                    });

                    // Succesful
                    if (success) {
                        setSaveLoading(false);
                        setSaveError(false);
                        popSuccessToast();
                    }

                    reload();
                }}
            >
                {t`Save changes`}
            </Button>
            <div aria-live="polite">
                {isSaveError && !hideSaveErrorMessage && (
                    <Caption style={styles.error}>
                        {t`Failed to save changes`}
                    </Caption>
                )}
            </div>
        </View>
    );
}

const popSuccessToast = () => {
    import("../notifications-package/toast-notification.tsx").then(
        ({renderChangesSavedToast}) => {
            renderChangesSavedToast();
        },
    );
};

const styles = StyleSheet.create({
    error: {
        color: color.red,
        height: "100%",
        marginLeft: spacing.small_12,
        // This matches the margin on the button. See the note above for why
        // we're not using a WonderBlocks component.
        marginTop: 10,
    },
    header: {
        color: color.offBlack,
    },
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
});
